import { useState } from 'react';
import { IonIcon } from '@ionic/react';
import { checkmarkCircle, chevronForward, close } from 'ionicons/icons';
import type { ProfileStrength } from '@/types';

/**
 * Get Started quest — first-run checklist for new candidates.
 *
 * Six steps mirror the ProfileStrength section ids (single source of truth).
 * Deep links route to existing editors; progress comes from the strength
 * endpoint. Dismissal is remembered per browser; `forceShow` (from
 * `?welcome=1`) overrides it once after signup.
 *
 * @see sprints/2026-10/Milestone_09_BluePagesCandidateGamification/sprint-02-onboarding-quest
 * @see Documentation/Specification/020_Definitions/application-specific/workseeking/ProfileStrength.md
 */
export type QuestStepId = 'photo' | 'headline-bio' | 'skills' | 'experience' | 'preferences' | 'verify';

interface QuestStep {
  id: QuestStepId;
  label: string;
  sections: string[];
}

const QUEST_STEPS: QuestStep[] = [
  { id: 'photo', label: 'Add a profile photo', sections: ['photo'] },
  { id: 'headline-bio', label: 'Write your headline and bio', sections: ['headline', 'bio'] },
  { id: 'skills', label: 'List your skills', sections: ['skills'] },
  { id: 'experience', label: 'Add work experience', sections: ['experience'] },
  { id: 'preferences', label: 'Set work preferences', sections: ['preferences'] },
  { id: 'verify', label: 'Verify your identity', sections: ['verified'] },
];

const DISMISS_KEY = 'bp-quest-dismissed';

function readDismissed(): boolean {
  try {
    return typeof localStorage !== 'undefined' && localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function writeDismissed(): void {
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem(DISMISS_KEY, '1');
  } catch {
    /* storage unavailable — quest simply reappears next visit */
  }
}

export function stepFilled(step: QuestStep, strength: ProfileStrength): boolean {
  return step.sections.every((id) => strength.sections.find((s) => s.id === id)?.filled);
}

interface GetStartedQuestProps {
  strength?: ProfileStrength | null;
  /** `?welcome=1` after signup — show even if previously dismissed. */
  forceShow?: boolean;
  onStepClick?: (step: QuestStepId) => void;
}

export function GetStartedQuest({ strength, forceShow = false, onStepClick }: GetStartedQuestProps) {
  const [dismissed, setDismissed] = useState(readDismissed);

  if (!strength || !Array.isArray(strength.sections) || strength.sections.length === 0) {
    return null;
  }

  const filled = QUEST_STEPS.filter((s) => stepFilled(s, strength));
  const allDone = filled.length === QUEST_STEPS.length;

  if (allDone) {
    return (
      <div className="bp-card" data-testid="quest-done" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 10 }}>
        <IonIcon icon={checkmarkCircle} style={{ color: 'var(--bp-success, #22c55e)', fontSize: '1.4rem' }} />
        <div>
          <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#f1f5f9' }}>Profile complete</div>
          <div style={{ fontSize: '0.75rem', color: 'var(--bp-text-muted, #64748b)' }}>
            All Get Started steps done. Keep your profile fresh to stay visible.
          </div>
        </div>
      </div>
    );
  }

  if (!forceShow && dismissed) {
    return null;
  }

  const dismiss = () => {
    writeDismissed();
    setDismissed(true);
  };

  return (
    <div className="bp-card" data-testid="quest-card" style={{ padding: '14px 16px', position: 'relative' }}>
      <button
        type="button"
        aria-label="Dismiss Get Started"
        onClick={dismiss}
        style={{
          position: 'absolute', top: 8, right: 8, background: 'transparent', border: 'none',
          color: 'var(--bp-text-muted, #64748b)', cursor: 'pointer', padding: 4,
        }}
      >
        <IonIcon icon={close} style={{ fontSize: '1rem' }} />
      </button>

      <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#f1f5f9', marginBottom: 2 }}>
        Get started
      </div>
      <div style={{ fontSize: '0.75rem', color: 'var(--bp-text-muted, #64748b)', marginBottom: 10 }}>
        {filled.length} of {QUEST_STEPS.length} steps done — a complete profile gets more views.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {QUEST_STEPS.map((step, idx) => {
          const done = stepFilled(step, strength);
          return (
            <button
              key={step.id}
              type="button"
              data-testid={`quest-step-${step.id}`}
              onClick={() => onStepClick?.(step.id)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                background: done ? 'rgba(34,197,94,0.10)' : 'rgba(255,255,255,0.04)',
                border: 'none', borderRadius: 10, padding: '8px 10px',
                cursor: 'pointer', textAlign: 'left',
              }}
            >
              <span
                style={{
                  width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.7rem', fontWeight: 700,
                  background: done ? 'var(--bp-success, #22c55e)' : 'rgba(255,255,255,0.08)',
                  color: done ? '#0b1220' : 'var(--bp-text-muted, #64748b)',
                }}
              >
                {done ? '✓' : idx + 1}
              </span>
              <span style={{ flex: 1, fontSize: '0.8rem', color: done ? 'var(--bp-success, #22c55e)' : '#e2e8f0' }}>
                {step.label}
              </span>
              <IonIcon icon={chevronForward} style={{ color: 'var(--bp-text-muted, #64748b)', fontSize: '0.9rem' }} />
            </button>
          );
        })}
      </div>
    </div>
  );
}
